
import { useState, useEffect, useRef, useMemo } from "react";
import { Button } from "../../Components/UI/button";
import { Input } from "../../Components/UI/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../Components/UI/table";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../Components/UI/card";
import {
  Search,
  Plus,
  User,
  Loader2,
  Filter,
  MapPin,
  ArrowUp,
  ArrowDown,
  ChevronUp,
  ChevronDown,
  BarChart3,
} from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from "../../Components/UI/tooltip";
import Pagination from "../../Components/Common/Pagination";
import {
  currentUserHasPermission,
  getOtherYearOptions,
  getPastMonthOptions,
  getPastQuarterOptions,
  getRelativeDateText,
  getThreadTypeMeta,
  isRecordInDateRange,
  months,
} from "../../Utils/Utilities";
import {
  format,
  addMonths,
  isSameDay,
  startOfWeek,
  endOfWeek,
  subDays,
  isWithinInterval,
  startOfDay,
  endOfDay,
  startOfMonth
} from "date-fns";
import useIsMobile from "../../Hooks/useIsMobile";
import DrawerFilter from "../../Components/Common/DrawerFilter";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
} from "../../Components/UI/drawer";
import { RiResetLeftFill } from "react-icons/ri";
import { useLocation, useNavigate } from "react-router-dom";
import ConnectionEntryDialog from "../Connections/ConnectionEntry";
import { CommonFilter } from "../../Components/Common/CommonFilter";
import PeriodFilter from "../../Components/Common/PeriodFilter";
import { useDispatch, useSelector } from "react-redux";
import {
  checkIfInqOnlyMode,
  getThreadTypesArr,
  getUsersArr,
  isSmartSellerMode,
} from "../../Store/Reducers";
import { setGlobalLoading } from "../../Store/Actions";
import { getRecordsAPI } from "./RecordAPI";
import RecordEntry from "./RecordsEntry";
import { cn } from "../../Lib/Cn";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "../../Components/UI/hover-card";
import ThreadSahiIcon from "../../Components/Common/ThreadSahiIcon";
import { Badge } from "../../Components/UI/badge";
import { toast } from "sonner";
// Recharts Imports
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  CartesianGrid,
  LabelList,
  Legend,
  Cell,
} from "recharts";
import ConditionalDiv from "../../Components/Common/ConditionalDiv";


const getCurrentMonthYear = () => format(new Date(), "MMMM yyyy");
const getTodayDateStr = () => format(new Date(), "yyyy-MM-dd");
const colorPalette = [
  "#0c6980",
  "#0b5d1e",
  "#d4af37",
  "#bf5700",
  "#230e2eff",
  "#c44536",
  "#2a9d8f",
  "#46210fff",
  "#3d348b",
  "#264653",
];
const Records = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isMobile = useIsMobile();
  const searchParams = new URLSearchParams(location.search);
  const isInqOnlyMode = useSelector(checkIfInqOnlyMode);
  // Redux Selectors
  const usersFromRedux = useSelector(getUsersArr);
  const recordTypesData = useSelector(
    (state) => state.masterData.recordTypes?.filter((rt) => rt.IsInxn) || []
  );
  // NOTE: This is still used for the Filter Dropdown, but NOT for the chart anymore.
  const ACTIVITY_TYPES_MASTER = recordTypesData?.map((item) => item.Typ) || [];
  const { data: connectionData } = useSelector((state) => state.connectionData);
  const {
    data: threadData,
    loading,
    error,
  } = useSelector((state) => state.threadData);
  const threadTypesObj = useSelector(getThreadTypesArr);
  const minDateRef = useRef(new Date()); // Add this line
  const [recordsData, setRecordsData] = useState([]);
  const [filteredRecords, setFilteredRecords] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [isFilterSelected, setIsFilterSelected] = useState(!isMobile);
  const [isConnectionDialogOpen, setIsConnectionDialogOpen] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [connData, setConnData] = useState([]);
  const [recEntry, setRecEntry] = useState(false);
  const [sortConfig, setSortConfig] = useState({
    key: "recDT",
    direction: "desc",
  });

  const oldestSinceDT = useMemo(() => {
    if (!connectionData || connectionData.length === 0) return new Date();

    const dates = connectionData
      .map((conn) => {
        // Use SinceDT if available, otherwise fallback to CreatedDT
        const dateStr = conn.SinceDT || conn.CreatedDT;
        return dateStr ? new Date(dateStr) : null;
      })
      .filter((d) => d !== null && !isNaN(d.getTime()));

    if (dates.length === 0) return new Date();

    // Return the oldest date found
    return new Date(Math.min(...dates));
  }, [connectionData]);

  const connViewTypeID = currentUserHasPermission('ConnViewTypeID');
  const isAssignedOnly = connViewTypeID === 4;

  const [tempSortConfig, setTempSortConfig] = useState(sortConfig);
  // --- CHART STATE ---
  const [activeChartFilters, setActiveChartFilters] = useState([]);
  const getInitialFilters = () => {
    const params = new URLSearchParams(location.search);
    const currentMY = getCurrentMonthYear();
    const todayStr = getTodayDateStr();

    return {
      recordType: params.get("iid") || "all",
      executive: params.get("eid") || "all",
      connID: params.get("cid") || "all",
      dateRange: params.get("p") || "Today",
      // Prioritize URL, otherwise strict current Month Year
      otherMonth: params.get("om") || currentMY,
      otherQuarter: params.get("oq") || "all",
      otherYear: params.get("oy") || "all",
      // Prioritize URL, otherwise strict today's date
      otherDate: params.get("od") || todayStr,
    };
  };
  const loadedParamsRef = useRef(null);
  const [selectedFilters, setSelectedFilters] = useState(getInitialFilters());
  const [tempFilters, setTempFilters] = useState(selectedFilters);
  const prevFiltersRef = useRef({});
  const isMonthlyView =
    selectedFilters.dateRange === "This Month" ||
    selectedFilters.dateRange === "Other Month";
  const currentYear = new Date().getFullYear();
  // 1. First, extract the unique types and map colors at the top level
  // const dynamicActivityTypes = useMemo(() => {
  //   if (!filteredRecords || filteredRecords.length === 0) return [];
  //   const typeTotals = {};
  //   filteredRecords.forEach((record) => {
  //     const rawType = record.recType?.trim() || "Unknown";
  //     typeTotals[rawType] = (typeTotals[rawType] || 0) + 1;
  //   });
  //   return Object.keys(typeTotals).sort(
  //     (a, b) => typeTotals[b] - typeTotals[a]
  //   );
  // }, [filteredRecords]);
  // 2. Define the color map at the top level (Fixes the "not defined" error)
  // const dynamicColorMap = useMemo(() => {
  //   const map = {};
  //   dynamicActivityTypes.forEach((type, index) => {
  //     map[type] = colorPalette[index % colorPalette.length];
  //   });
  //   return map;
  // }, [dynamicActivityTypes]);
  const { processedChartData, maxSegments, dynamicActivityTypes, dynamicColorMap } = useMemo(() => {
    if (!filteredRecords || filteredRecords.length === 0) {
      return { processedChartData: [], maxSegments: 0, dynamicActivityTypes: [], dynamicColorMap: {} };
    }
    // 1. Fixed Color Map for the Legend (so colors stay consistent for types)
    const typeTotals = {};
    filteredRecords.forEach(r => {
      const t = r.recType?.trim() || "Unknown";
      typeTotals[t] = (typeTotals[t] || 0) + 1;
    });
    const sortedMasterTypes = Object.keys(typeTotals).sort((a, b) => typeTotals[b] - typeTotals[a]);
    const colorMap = {};
    sortedMasterTypes.forEach((type, index) => {
      colorMap[type] = colorPalette[index % colorPalette.length];
    });
    // 2. Group by Date
    const dateMap = {};
    filteredRecords.forEach((record) => {
      const dateKey = format(new Date(record.recDT), "MMM dd");
      const rawType = record.recType?.trim() || "Unknown";
      if (!dateMap[dateKey]) dateMap[dateKey] = { name: dateKey, counts: {} };
      dateMap[dateKey].counts[rawType] = (dateMap[dateKey].counts[rawType] || 0) + 1;
    });
    // 3. Transform and Sort segments per row
    let globalMaxSegments = 0;
    const processed = Object.values(dateMap).map((dateGroup) => {
      const resultRow = { name: dateGroup.name, total: 0 };
      // Create segments for this specific date
      const segments = Object.entries(dateGroup.counts)
        .map(([label, value]) => ({ label, value, color: colorMap[label] }))
        // Filter by legend
        .filter(s => activeChartFilters.length === 0 || activeChartFilters.includes(s.label))
        // SORT: Largest value first (shows closest to Y-axis)
        .sort((a, b) => b.value - a.value);
      if (segments.length > globalMaxSegments) globalMaxSegments = segments.length;
      segments.forEach((seg, index) => {
        resultRow[`stack${index}`] = seg.value;
        resultRow[`stack${index}Color`] = seg.color;
        resultRow[`stack${index}Label`] = seg.label;
        resultRow.total += seg.value;
      });
      return resultRow;
    }).sort((a, b) => {
      return new Date(b.name + " " + currentYear).getTime() - new Date(a.name + " " + currentYear).getTime();
    });
    return {
      processedChartData: processed,
      maxSegments: globalMaxSegments,
      dynamicActivityTypes: sortedMasterTypes,
      dynamicColorMap: colorMap
    };
  }, [filteredRecords, activeChartFilters, currentYear]);
  const chartHeight = useMemo(() => {
    const barHeight = 45;
    const padding = 40;
    const calculated = processedChartData.length * barHeight + padding;
    // Set minimum to 180 to give the Tooltip logic enough room to render
    return processedChartData.length > 0 ? Math.max(calculated, 180) : 0;
  }, [processedChartData]);
  const toggleChartFilter = (type) => {
    setActiveChartFilters((prev) => {
      if (prev.includes(type)) return prev.filter((t) => t !== type);
      return [...prev, type];
    });
  };
  const visitTypeID = useMemo(() => {
    const visitType = recordTypesData?.find(
      (rt) => rt.Typ === "Visit" || rt.Typ === "Visit Log" || rt.Typ === "visit"
    );
    return visitType ? String(visitType.TID) : null;
  }, [recordTypesData]);

  const isVisitLogMode = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const currentID = selectedFilters.recordType !== "all"
      ? selectedFilters.recordType
      : params.get("iid");

    return currentID && currentID === visitTypeID;
  }, [selectedFilters.recordType, location.search, visitTypeID]);

  const pageTitle = isVisitLogMode ? "Visit Log" : "Team Efforts Log";


  useEffect(() => {
    document.title = pageTitle;

    return () => {
      document.title = "SahiClub";
    };
  }, [pageTitle]);

  useEffect(() => {
    prevFiltersRef.current = getInitialFilters();
  }, []);
  const mobileSortMap = {
    "Date (newest first)": { key: "RecDT", direction: "desc" },
    "Date (oldest first)": { key: "RecDT", direction: "asc" },
    Type: { key: "RecType", direction: "asc" },
    Executive: { key: "RecUserID", direction: "asc" },
  };
  const sortedExecutives = [...usersFromRedux]
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((u) => ({ name: u.name, ID: u.userID }));
  // 2. Pre-sort Record Types
  const sortedRecordTypes = [...(recordTypesData || [])]
    .sort((a, b) => a.Typ.localeCompare(b.Typ))
    .map((rt) => ({ name: rt.Typ, ID: "" + rt.TID }));
  const filterOptions = useMemo(() => [
    {
      key: "dateRange",
      label: "Period",
      options: ["Today", "Yesterday", "This Week", "Last Week", "This Month", "Other Month", "Other Date"],
      type: "options",
    },
    {
      key: "otherMonth",
      label: "Select Month",
      options: getPastMonthOptions(oldestSinceDT),
      displayValue: (val) => {
        if (!val || val === "all") return getCurrentMonthYear();
        return val;
      },
      type: "list",
      disabled: tempFilters.dateRange !== "Other Month",
      mobiledisabled: tempFilters.dateRange !== "Other Month",
    },
    {
      key: "otherDate",
      label: "Select Date",
      type: "date",
      displayValue: (val) => (!val || val === "all") ? getTodayDateStr() : val,
      disabled: tempFilters.dateRange !== "Other Date",
      mobiledisabled: tempFilters.dateRange !== "Other Date",
      mobileDropdownText: "Select Date",
      mobileNoLabel: true,
    },
    ...(!isSmartSellerMode() && !isAssignedOnly ? [{
      key: "executive",
      label: "All Executives",
      options: [
        { name: "Not Assigned", ID: "unassigned" },
        ...sortedExecutives,
      ],
      displayValue: (val) => {
        if (val === "all") return "All Executives";
        if (val === "unassigned") return "Not Assigned";
        return usersFromRedux.find((u) => u.userID.toString() === val.toString())?.name || "All Executives";
      },
      type: "list",
      searchEnabled: true,
      mobileDropdownText: "Select Executive",
      mobileLabel: "Executive",
      showAllOption: true,
    }] : []),
    {
      key: "sort",
      label: "Sort By",
      options: Object.keys(mobileSortMap),
      type: "sort",
    },
    {
      key: "recordType",
      label: "Type",
      options: sortedRecordTypes,
      displayValue: (val) =>
        recordTypesData.find((r) => r.TID.toString() === val.toString())?.Typ || "All Types",
      type: "list",
      searchEnabled: true,
      mobileDropdownText: "Select Type",
      mobileLabel: "Type",
      showAllOption: true,
    },
    {
      key: "connID",
      label: "All Contacts",
      options: connData.map((c) => ({
        name: c.bizName || c.contactName,
        ID: c.connID,
      })),
      displayValue: (val) => {
        const c = connData.find((c) => Number(c.connID) === Number(val));
        return c ? c.bizName || c.contactName : "All Contacts";
      },
      type: "list",
      searchEnabled: true,
      mobileDropdownText: "Select Contact",
      mobileLabel: "Contact",
      showAllOption: true,
    },
  ], [
    oldestSinceDT,
    tempFilters.dateRange,
    sortedExecutives,
    sortedRecordTypes,
    connData,
    isVisitLogMode
  ]);
  useEffect(() => {
    if (recEntry !== false) return;

    const p = selectedFilters.dateRange;
    const now = new Date();
    let targetYear = now.getFullYear().toString();
    let targetMonth = (now.getMonth() + 1).toString();
    let targetPrdType = "MM";
    let targetPrdQty = "1";
    let targetRecTypeID = selectedFilters.recordType !== "all" ? selectedFilters.recordType : "0";

    // 1. Calculate the actual API parameters based on UI selection
    if (p === "Yesterday" || p === "This Week" || p === "Last Week") {
      targetPrdQty = "2";
    }
    else if (p === "Other Month") {
      const monthToUse = selectedFilters.otherMonth;
      if (monthToUse && monthToUse !== "all") {
        const parts = monthToUse.split(" ");
        if (parts.length === 2) {
          const mIndex = months.indexOf(parts[0]);
          if (mIndex !== -1) {
            targetMonth = (mIndex + 1).toString();
            targetYear = parts[1];
          }
        }
      }
    }
    else if (p === "Other Date") {
      const dateToUse = selectedFilters.otherDate;
      if (dateToUse && dateToUse !== "all") {
        const selectedDate = new Date(dateToUse);
        targetYear = selectedDate.getFullYear().toString();
        targetMonth = (selectedDate.getMonth() + 1).toString();
      }
    }
    else if (p === "Other Quarter" && selectedFilters.otherQuarter !== "all") {
      const [q, y] = selectedFilters.otherQuarter.split(" ");
      targetPrdType = q;
      targetYear = y;
    }

    // 2. CHECK: Are these parameters different from what we already have?
    const currentParams = `${targetYear}-${targetMonth}-${targetPrdType}-${targetPrdQty}-${targetRecTypeID}`;

    if (loadedParamsRef.current === currentParams && recordsData.length > 0) {
      // Parameters are identical (e.g. "This Month" and "Other Month (Current)")
      // Stop here, no API call needed.
      return;
    }

    const fetchRecordsData = async () => {
      dispatch(setGlobalLoading(true));
      try {
        const params = {
          recTypeID: targetRecTypeID,
          prdType: targetPrdType,
          prdQty: targetPrdQty,
          year: targetYear,
          month: targetMonth,
        };

        const rawRecords = await getRecordsAPI(params);
        const data = rawRecords.filter((item) => item.RecTypeID !== 0);
        setConnData(() => {
          const uniqueConns = {};
          data.forEach((rec) => {
            if (rec.ConnID && !uniqueConns[rec.ConnID]) {
              const connInfo = connectionData.find((c) => c.ConnID === rec.ConnID);
              if (connInfo) {
                uniqueConns[rec.ConnID] = { 
                  connID: rec.ConnID,
                  bizName: connInfo.BizName || "",
                  contactName: connInfo.ContactName || "",
                };
              }
            }
          });
          return Object.values(uniqueConns);
        });
        console.log("Fetched Records Data:", connData);

        // Update the Ref ONLY after a successful fetch
        loadedParamsRef.current = currentParams;
        setRecordsData(getProcessedRecs(data));
      } catch (error) {
        console.error("Error:", error);
        toast.error("Failed to load records");
      } finally {
        dispatch(setGlobalLoading(false));
      }
    };

    fetchRecordsData();
  }, [
    recEntry,
    selectedFilters.dateRange,
    selectedFilters.otherMonth,
    selectedFilters.otherQuarter,
    selectedFilters.recordType,
    selectedFilters.otherDate,
  ]);

  const getProcessedRecs = (recs) => {
    let currentMinTs = minDateRef.current.getTime();
    let foundNewOldest = false;

    // Process all records in a single map loop
    const processed = recs.map((r) => {
      // 1. TRACK OLDEST DATE
      if (r.RecDT) {
        const recordTs = new Date(r.RecDT).getTime();
        if (!isNaN(recordTs) && recordTs < currentMinTs) {
          currentMinTs = recordTs;
          foundNewOldest = true;
        }
      }

      // 2. FIND CONNECTION DATA
      const conn = connectionData.find((c) => c.ConnID === r.ConnID) || {};

      // 3. PARSE TASKS
      const tasks = (() => {
        try {
          return r.Tasks && r.Tasks.trim() !== "" ? JSON.parse(r.Tasks) : [];
        } catch {
          return [];
        }
      })();

      // 4. PROCESS THREAD IDS
      let threadNames = [],
        threadTypes = [],
        threadDueDates = [],
        threadSinceDates = [],
        threadTypeIDs = [];

      if (r.ThreadIDs?.trim()) {
        const threadIDs = r.ThreadIDs.split(",").map((id) => id.trim());
        threadIDs.forEach((threadId) => {
          const thread = threadData.find(
            (t) => t.ThreadID?.toString() === threadId
          );
          if (thread) {
            const threadTypeMeta = getThreadTypeMeta(
              thread.TypeID,
              threadTypesObj
            );
            threadTypes.push(threadTypeMeta?.nameSng || "General");
            threadNames.push(
              thread.MainProduct ||
              thread.product ||
              thread.Product ||
              "No Offering"
            );
            threadDueDates.push(thread.DueDT || thread.targetDT || "");
            threadSinceDates.push(thread.SinceDT || thread.sinceDT || "");
            threadTypeIDs.push(thread.TypeID);
          }
        });
      }

      // 5. RETURN TRANSFORMED OBJECT
      return {
        recordID: r.RecordID,
        record: r.Record,
        recDT: r.RecDT,
        recTypeID: r.RecTypeID,
        recType: r.RecType,
        recUserID: r.RecUserID,
        isInxn: r.IsInxn,
        loc: r.Loc,
        threadIDs: r.ThreadIDs,
        refTaskID: r.RefTaskID,
        connID: r.ConnID,
        bizName: conn.BizName || "",
        mobile: conn.Mobile || "",
        email: conn.Email || "",
        connType: conn.ConnType || "",
        contactName: conn.ContactName || "",
        tasks,
        threadNames: threadNames.join(", "),
        threadTypes: threadTypes.join(", "),
        threadDueDates: threadDueDates.join(", "),
        threadSinceDates: threadSinceDates.join(", "),
        threadTypeIDs: threadTypeIDs.join(", "),
      };
    });

    // Update the oldest date state if a new one was found after processing all records
    if (foundNewOldest) {
      const newOldest = new Date(currentMinTs);
      minDateRef.current = newOldest;
    }

    return processed;
  };

  useEffect(() => {
    const currentParams = new URLSearchParams(location.search);
    const urlMap = {
      connID: "cid",
      executive: "eid",
      recordType: "iid",
      dateRange: "p",
      otherMonth: "om",
      otherQuarter: "oq",
      otherYear: "oy",
      otherDate: "od",
    };
    Object.entries(urlMap).forEach(([stateKey, urlKey]) => {
      const val = selectedFilters[stateKey];
      if (val && val !== "all") currentParams.set(urlKey, val);
      else currentParams.delete(urlKey);
    });
    const newUrl = currentParams.toString()
      ? `${location.pathname}?${currentParams.toString()}`
      : location.pathname;
    if (newUrl !== location.pathname + location.search)
      navigate(newUrl, { replace: true });
  }, [selectedFilters, navigate, location.pathname]);
  const sortData = (key, dir) => {
    if (!key && !dir) return;
    if (key === "threadIDs") return;
    let direction =
      dir ??
      (sortConfig.key === key && sortConfig.direction === "asc"
        ? "desc"
        : "asc");
    setSortConfig({ key, direction });
    const sorted = [...filteredRecords].sort((a, b) => {
      let aValue = a[key] || "";
      let bValue = b[key] || "";
      if (key === "recDT") {
        aValue = aValue ? new Date(aValue).getTime() : 0;
        bValue = bValue ? new Date(bValue).getTime() : 0;
        return direction === "asc" ? aValue - bValue : bValue - aValue;
      }
      if (key === "recUserID") {
        aValue = getExecutiveName(aValue) || "";
        bValue = getExecutiveName(bValue) || "";
      }
      aValue = aValue.toString().toLowerCase();
      bValue = bValue.toString().toLowerCase();
      return direction === "asc"
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    });
    setFilteredRecords(sorted);
  };

  const applyFilters = () => {
    dispatch(setGlobalLoading(true));
    const { recordType, executive, connID, dateRange, otherDate } = selectedFilters;
    const baseList = recordsData;
    const filtered = [];
    for (const record of baseList) {
      const recordDate = new Date(record.recDT);
      const today = startOfDay(new Date());
      // --- 1. Connection Filter ---
      if (connID !== "all" && record.connID && record.connID.toString() !== connID.toString()) continue;
      // --- 2. Record Type Filter ---
      if (recordType !== "all" && String(record.recTypeID) !== String(recordType)) continue;
      // --- 3. Executive Filter ---
      if (executive !== "all") {
        if (executive === "unassigned") {
          if (record.recUserID) continue;
        } else {
          if (!record.recUserID || record.recUserID.toString() !== executive.toString()) continue;
        }
      }
      let isInRange = false;
      if (dateRange === "Today") {
        isInRange = isSameDay(recordDate, today);
      } else if (dateRange === "Yesterday") {
        isInRange = isSameDay(recordDate, subDays(today, 1));
      } else if (dateRange === "This Week") {
        isInRange = isWithinInterval(recordDate, {
          start: startOfWeek(today, { weekStartsOn: 1 }),
          end: endOfDay(today),
        });
      } else if (dateRange === "Last Week") {
        const lastWeekStart = startOfWeek(subDays(today, 7), { weekStartsOn: 1 });
        const lastWeekEnd = endOfWeek(subDays(today, 7), { weekStartsOn: 1 });
        isInRange = isWithinInterval(recordDate, { start: lastWeekStart, end: lastWeekEnd });
      }
      // Locate this part in your applyFilters function:
      else if (dateRange === "Other Date") {
        if (otherDate && otherDate !== "all") {
          // Strict match for that specific day only
          isInRange = isSameDay(startOfDay(recordDate), startOfDay(new Date(otherDate)));
        } else {
          // If 'Other Date' is selected but no 'otherDate' is picked, hide everything
          isInRange = false;
        }
      }
      else {
        isInRange = isRecordInDateRange(
          record.recDT,
          dateRange,
          "",
          selectedFilters.otherMonth,
          selectedFilters.otherQuarter,
          selectedFilters.otherYear
        );
      }

      if (!isInRange) continue;
      // --- 5. Search Bar ---
      if (searchTerm && searchTerm.trim()) {
        const term = searchTerm.toLowerCase().trim();
        const matchesSearch = [
          record.recType || "",
          record.record || "",
          record.bizName || "",
          record.threadNames || "",
          getExecutiveName(record.recUserID) || "",
        ].some((field) => field.toLowerCase().includes(term));
        if (!matchesSearch) continue;
      }
      filtered.push(record);
    }
    setFilteredRecords(filtered);
    setCurrentPage(1);
    dispatch(setGlobalLoading(false));
  };

  // useEffect(() => {
  //   if (isMobile && isDrawerOpen) return;
  //   const params = new URLSearchParams(location.search);
  //   const currentMY = getCurrentMonthYear();
  //   const todayStr = getTodayDateStr();

  //   const newFiltersFromUrl = {
  //     connID: params.get("cid") || "all",
  //     executive: params.get("eid") || "all",
  //     recordType: params.get("iid") || "all",
  //     dateRange: params.get("p") || "Today",
  //     otherMonth: params.get("om") || currentMY,
  //     otherQuarter: params.get("oq") || "all",
  //     otherYear: params.get("oy") || "all",
  //     otherDate: params.get("od") || todayStr,
  //   };

  //   // Deep comparison check to break the infinite loop
  //   const isDifferent = Object.keys(newFiltersFromUrl).some(
  //     (key) => newFiltersFromUrl[key] !== selectedFilters[key]
  //   );

  //   if (isDifferent) {
  //     setSelectedFilters(newFiltersFromUrl);
  //     setTempFilters(newFiltersFromUrl);
  //   }
  // }, [location.search ,isDrawerOpen]); // Remove selectedFilters from here

  // --- REPLACE YOUR URL SYNC EFFECT WITH THIS ---
  useEffect(() => {
    // CRITICAL FIX: If the mobile drawer is open, we do NOT let URL parameters 
    // touch the state. We only sync when the drawer is CLOSED.
    if (isMobile && isDrawerOpen) return;

    const params = new URLSearchParams(location.search);
    const currentMY = getCurrentMonthYear();
    const todayStr = getTodayDateStr();

    const newFiltersFromUrl = {
      connID: params.get("cid") || "all",
      executive: params.get("eid") || "all",
      recordType: params.get("iid") || "all",
      dateRange: params.get("p") || "Today",
      otherMonth: params.get("om") || currentMY,
      otherQuarter: params.get("oq") || "all",
      otherYear: params.get("oy") || "all",
      otherDate: params.get("od") || todayStr,
    };

    // Compare incoming URL values to the CURRENT "Selected" state (not temp state)
    const isDifferent = Object.keys(newFiltersFromUrl).some(
      (key) => String(newFiltersFromUrl[key]) !== String(selectedFilters[key])
    );

    // CRITICAL: Only sync if there's a difference AND we're not in "Other Date" mode
    // This prevents the URL from overwriting user's date selection
    if (isDifferent) {
      // If we're in Other Date mode and ONLY the otherDate changed, ignore it
      if (selectedFilters.dateRange === "Other Date") {
        const onlyDateChanged = Object.keys(newFiltersFromUrl).every(key => {
          if (key === "otherDate") return true; // Allow this to differ
          return String(newFiltersFromUrl[key]) === String(selectedFilters[key]);
        });

        if (onlyDateChanged) return; // Don't sync if only date changed
      }

      // Only update if there's a real change to avoid loop
      setSelectedFilters(newFiltersFromUrl);
      setTempFilters(newFiltersFromUrl);
    }
  }, [location.search, isMobile, isDrawerOpen]);
  // Added selectedFilters.dateRange to dependencies
  // removed selectedFilters from dependencies to break the recursion loop
  useEffect(() => {
    applyFilters();
  }, [recordsData, selectedFilters, searchTerm]);

  // Add/Update this inside the Records component
  useEffect(() => {
    const handleHeaderFilterClick = () => {
      if (isMobile) {
        setIsDrawerOpen(true);
        setIsFilterSelected(true);
      } else {
        setIsFilterSelected((prev) => !prev);
      }
    };

    // Match the event name from your config: "open-team-efforts-log-filter"
    window.addEventListener("open-team-efforts-log-filter", handleHeaderFilterClick);
    return () => window.removeEventListener("open-team-efforts-log-filter", handleHeaderFilterClick);
  }, [isMobile]);

  const totalPages = Math.ceil(filteredRecords.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const currentRecords = useMemo(() => {
    // If no manual header click has happened yet, we force Date Descending
    let sorted = [...filteredRecords];
    if (sortConfig.key === "recDT") {
      sorted.sort((a, b) => {
        const dateA = a.recDT ? new Date(a.recDT).getTime() : 0;
        const dateB = b.recDT ? new Date(b.recDT).getTime() : 0;
        // Descending: Newest (larger number) first
        return sortConfig.direction === "desc" ? dateB - dateA : dateA - dateB;
      });
    } else if (!sortConfig.key) {
      // Fallback if somehow config is cleared
      sorted.sort((a, b) => new Date(b.recDT) - new Date(a.recDT));
    } else {
      // This handles other columns if you click them (Executive, Contacts, etc.)
      // Your sortData function already handles updating filteredRecords for these
      sorted = filteredRecords;
    }
    if (isMobile) return sorted;
    const sliced = sorted.slice(startIndex, endIndex);
    return sliced.length > 0 ? sliced : sorted;
  }, [filteredRecords, sortConfig, currentPage, rowsPerPage, isMobile]);
  function getExecutiveName(userID) {
    if (!userID) return "Not Assigned";
    return (
      usersFromRedux.find((u) => u.userID === userID)?.name || "Not Assigned"
    );
  }
  const isValidCoordinate = (loc) => {
    if (!loc || typeof loc !== "string") return false;
    const trimmed = loc.trim();
    // Checks if empty, just commas, or the common "empty" coordinate 0,0
    return (
      trimmed !== "" &&
      trimmed !== "0,0" &&
      trimmed.replace(/,/g, "").length > 0
    );
  };
  // const formatThreadDisplay = (record) => {
  //   const threadIDs = record.threadIDs;
  //   if (!threadIDs?.trim()) return null;
  //   const threadNames = record.threadNames?.split(", ") || [];
  //   const threadTypes = record.threadTypes?.split(", ") || [];
  //   const threadDueDates = record.threadDueDates?.split(", ") || [];
  //   const threadSinceDates = record.threadSinceDates?.split(", ") || [];
  //   const threadTypeIDs = record.threadTypeIDs?.split(", ") || [];
  //   return (
  //     <div className="flex flex-wrap gap-2 mt-2 lg:mt-0">
  //       {threadIDs.split(",").map((ID, index) => ID.trim()).map((threadId, index) => {
  //         let threadIcon = null; const typeID = threadTypeIDs[index];
  //         if (typeID) { const threadTypeMeta = getThreadTypeMeta(Number(typeID), threadTypesObj); if (threadTypeMeta) threadIcon = threadTypeMeta.Icon; }
  //         const displayText = threadNames[index] || `Thread #${threadId}`;
  //         const threadType = threadTypes[index] || "General";
  //         const hasDateInfo = threadDueDates[index] || threadSinceDates[index];
  //         const sinceDT = threadSinceDates[index] ? getRelativeDateText(new Date(threadSinceDates[index]), false, false) : "";
  //         const targetDT = threadDueDates[index] ? getRelativeDateText(new Date(threadDueDates[index]), false, false) : "";
  //         const BadgeContent = (<Button variant="ghost" size="sm" className="justify-start p-0 h-auto"><Badge className={`justify-start h-7 ${badgeBase} px-2`}>{threadIcon && (<ThreadSahiIcon tID={Number(typeID)} className="mr-2 flex-shrink-0" />)}<span className="truncate text-xs">{displayText}</span></Badge></Button>);
  //         if (displayText === " " || displayText === "-") return null;
  //         if (displayText.toLowerCase().startsWith("thread")) return null;
  //         return hasDateInfo ? (<HoverCard key={`thread-${threadId}`}><HoverCardTrigger asChild>{BadgeContent}</HoverCardTrigger><HoverCardContent className="bg-white sahi-radius shadow-2xl p-3 w-30 text-left" side="top"><h4 className="text-sm font-bold mb-2">{threadType}</h4><div>{targetDT && (<div><span className="text-muted-foreground">Due on: </span><span>{targetDT}</span></div>)}{sinceDT && (<div><span className="text-muted-foreground">Since: </span><span>{sinceDT}</span></div>)}</div></HoverCardContent></HoverCard>) : (<div key={`thread-${threadId}`}>{BadgeContent}</div>);
  //       })}
  //     </div>
  //   );
  // };
  const handleCancel = () => {
    setTempFilters(selectedFilters);
    setTempSortConfig(sortConfig);
    setIsDrawerOpen(false);
    setIsFilterSelected(false);
  };

  const handleResetFilters = () => {
    const params = new URLSearchParams(location.search);

    // 1. Identify if we should persist the current recordType (e.g., if in Visit Log mode)
    // If visitTypeID exists, we keep it; otherwise, we go back to "all"
    const persistentType = isVisitLogMode ? visitTypeID : "all";

    const currentMY = getCurrentMonthYear();
    const todayStr = getTodayDateStr();

    const clearedFilters = {
      recordType: persistentType,
      executive: "all",
      connID: "all",
      dateRange: "This Month", // Reset to default period
      otherMonth: currentMY,
      otherQuarter: "all",
      otherYear: "all",
      otherDate: todayStr,
    };

    // 2. Update all states
    setSelectedFilters(clearedFilters);
    setTempFilters(clearedFilters);
    setSortConfig({ key: "recDT", direction: "desc" });
    setSearchTerm("");
    setCurrentPage(1);

    // 3. Force the chart legend filters to clear as well
    setActiveChartFilters([]);

    // 4. Update URL - Clear all params except the necessary 'iid' and the default 'p'
    const newParams = new URLSearchParams();
    if (persistentType !== "all") {
      newParams.set("iid", persistentType);
    }
    newParams.set("p", "This Month");

    navigate(`${location.pathname}?${newParams.toString()}`, { replace: true });

    // 5. Success feedback
  };

  const FilterButton = () => {
    let activeFilterClass =
      isFilterSelected && !isMobile ? "filter-active" : "";
    let filterIconToggleClass =
      "h-4 w-4 " +
      (isMobile
        ? ""
        : isFilterSelected
          ? "text-sahi-primary-color"
          : "text-white group-hover:text-sahi-primary-color");

    // Add or Update this useMemo inside your Records component
    const hasActiveFilters = useMemo(() => {
      return Object.entries(selectedFilters).some(([key, value]) => {
        // 1. If we are in Visit Log mode, ignore the recordType filter
        if (isVisitLogMode && key === "recordType") return false;

        // 2. Ignore internal "other" filters if their parent range isn't active
        // if (key === "otherMonth" && selectedFilters.dateRange !== "Other Month") return false;
        // if (key === "otherQuarter" && selectedFilters.dateRange !== "Other Quarter") return false;
        // if (key === "otherYear" && selectedFilters.dateRange !== "Other Year") return false;
        // if (key === "otherDate" && selectedFilters.dateRange !== "Other Date") return false;

        // 3. Check if dateRange is anything other than the default "This Month"
        if (key === "dateRange") return value !== "This Month";

        // 4. For all other filters (executive, connID), check if they are not "all"
        return value !== "all";
      });
    }, [selectedFilters, isVisitLogMode]);
    return (
      <Button
        className={cn(
          "button-config sahi-primary-button flex items-center justify-center group px-2.5 py-[5px] relative",
          activeFilterClass
        )}
        onClick={() => {
          if (isMobile) {
            setIsDrawerOpen(true);
            setIsFilterSelected(true);
          } else {
            setIsFilterSelected(!isFilterSelected);
          }
        }}
        aria-label="Toggle Filter"
      >
        <div className="h-6 w-6 flex items-center justify-center transition-all">
          <Filter className={cn(filterIconToggleClass)} />
        </div>
        {hasActiveFilters && isMobile && (
          <span className="absolute -top-1 -right-1 h-2.5 w-2.5 bg-sahi-green rounded-full border border-sahi-white" />
        )}
      </Button>
    );
  };

  const MobileRecordCard = ({ record, index }) => {
    // Local state to toggle full text
    const [isExpanded, setIsExpanded] = useState(false);
    const hasValidLocation = isValidCoordinate(record.loc);

    return (
      <div
        key={record.recordID || index}
        className="bg-sahi-white sahi-radius shadow-sm border border-gray-200 mb-2 w-full flex flex-col justify-between"
      >
        <div className="f-row justify-between mobile-tile-header-layout !pt-[10px]">
          <div className="f-col justify-between gap-1 items-start w-full">
            <div className="f-row items-center justify-between w-full">
              <div className="f-row min-w-0 gap-1 items-center">
                {record.bizName && (
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-text-color break-words text-sm">
                      {record?.bizName.length > 26
                        ? record?.bizName.slice(0, 26) + "..."
                        : record?.bizName || "BizName"}
                    </span>
                  </div>
                )}
              </div>
            </div>
            <div className="f-col justify-between gap-1 items-start w-full">
              {!isVisitLogMode && (
                <span className="text-xs text-sub-text-color truncate font-medium">
                  {record.recType?.trim() || "Record"}
                </span>
              )}
              {record.record?.trim() && (
                <p
                  onClick={() => setIsExpanded(!isExpanded)}
                  className={cn(
                    "text-sm text-black break-words transition-all duration-200 cursor-pointer w-full",
                    isExpanded ? "whitespace-pre-wrap" : "line-clamp-2"
                  )}
                >
                  {record.record}
                </p>
              )}
            </div>
          </div>
        </div>
        <div className="flex h-[38px] justify-between items-center text-xs border-t border-[#f1f1f1] w-full mobile-tile-footer-layout px-2">
          <div className="flex items-center gap-2">
            <span className="font-medium">
              @ {getRelativeDateText(record.recDT)}
            </span>
            {hasValidLocation && (
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  const mapsUrl = `https://www.google.com/maps?q=${encodeURIComponent(
                    record.loc
                  )}`;
                  window.open(mapsUrl, "_blank");
                }}
                variant="ghost"
                size="sm"
                className="h-4 w-4 p-0 hover:bg-sahi-blue-light"
                title="View location on map"
              >
                <MapPin className="h-3.5 w-3.5 text-sahi-blue" />
              </Button>
            )}
          </div>
          <span className="flex items-center gap-1 text-sub-text-color">
            <User className="h-3.5 w-3.5" />
            {getExecutiveName(record.recUserID)}
          </span>
        </div>
      </div>
    );
  };

  const renderEmptyState = () => {

    const config = isVisitLogMode
      ? {
        icon: MapPin,
        description: "View visit logs with integrated map for location tracking.",
        subText: "No data available for this period.",
      }
      : {
        icon: BarChart3,
        description: "Track sales efforts, intentions, and rejection reasons across team members.",
        subText: "No data available for this period.",
      };

    const IconComponent = config.icon;
    return (
      <div className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg bg-white shadow-sm w-full max-w-[400px] md:min-w-[600px] md:w-fit">
        <div className="flex-shrink-0 p-2 bg-gray-50 rounded-md">
          <IconComponent className="h-6 w-6 text-gray-400" strokeWidth={2} />
        </div>
        <div className="flex flex-col gap-2">
          <span className="text-sm font-semibold text-gray-500 leading-tight">
            {config.description}
          </span>
          <span className="text-xs font-medium text-gray-900">
            {config.subText}
          </span>
        </div>
      </div>
    );
  };

  const CustomTooltip = ({ active, payload, label, position }) => {
    if (active && payload && payload.length) {
      return (
        <div 
          className="bg-white p-3 shadow-2xl rounded-lg border border-gray-200 min-w-[140px] z-[9999]" 
          style={{
            position: 'relative',
            pointerEvents: 'none'
          }}
        >
          <p className="text-black mb-2 border-b pb-1 font-bold text-sm">{label}</p>
          {payload.map((entry, index) => {
            if (!entry.value) return null;
            return (
              <div key={index} className="flex items-center gap-2 my-1">
                <div
                  className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: entry.payload[`${entry.dataKey}Color`] }}
                />
                <span className="text-xs font-medium text-gray-700">
                  {entry.payload[`${entry.dataKey}Label`]}:
                </span>
                <span className="text-xs ml-auto font-bold text-black">{entry.value}</span>
              </div>
            );
          })}
        </div>
      );
    }
    return null;
  };
  // Add or Update this useMemo inside your Records component
  const hasActiveFilters = useMemo(() => {
    return Object.entries(selectedFilters).some(([key, value]) => {
      // 1. If we are in Visit Log mode, ignore the recordType filter
      if (isVisitLogMode && key === "recordType") return false;

      // 2. Ignore internal "other" filters if their parent range isn't active
      if (key === "otherMonth" && selectedFilters.dateRange !== "Other Month") return false;
      if (key === "otherQuarter" && selectedFilters.dateRange !== "Other Quarter") return false;
      if (key === "otherYear" && selectedFilters.dateRange !== "Other Year") return false;
      if (key === "otherDate" && selectedFilters.dateRange !== "Other Date") return false;

      // 3. Check if dateRange is anything other than the default "This Month"
      if (key === "dateRange") return value !== "This Month";

      // 4. For all other filters (executive, connID), check if they are not "all"
      return value !== "all";
    });
  }, [selectedFilters, isVisitLogMode]);

  const showChart = useMemo(() => {
    const range = selectedFilters.dateRange;
    const dayPicked = selectedFilters.otherDate;

    // If Other Date mode is on, require a day to be picked before showing chart
    if (range === "Other Date" && (dayPicked === "all" || !dayPicked)) return false;

    const hideRanges = ["This Month", "Other Month"];
    return filteredRecords.length > 0 && !hideRanges.includes(range);
  }, [filteredRecords, selectedFilters.dateRange, selectedFilters.otherDate]);

  const tableHeaderCols = useMemo(() => {
    const isLargeRange = isMonthlyView;
    // If showChart is false, we want the table to be more expansive
    const chartVisible = showChart;

    if (isVisitLogMode) {
      return [
        { key: "recDT", label: "Date", className: "w-[140px]" },
        { key: "recUserID", label: "Executive", className: "w-[140px]" },
        {
          key: "bizName",
          label: "Contacts",
          // Allow more space if chart is hidden
          className: !chartVisible ? "w-[350px]" : (isLargeRange ? "w-[190px]" : "w-[200px]")
        },
        { key: "recType", label: "Location", className: "w-[150px]" },
        {
          key: "record",
          label: "Notes",
          // This column will now suck up all remaining space
          className: "w-auto"
        },
      ];
    }

    return [
      {
        key: "bizName",
        label: "Contact",
        className: !chartVisible ? "w-[400px]" : (isLargeRange ? "w-[150px]" : "w-[180px]"),
      },
      {
        key: "recType",
        label: "Type",
        className: "w-[150px]",
      },
      {
        key: "record",
        label: "Notes",
        className: "w-auto", // Let it expand
      },
      { key: "recDT", label: "Date", className: "w-[150px]" },
      {
        key: "recUserID",
        label: "Executive",
        className: "w-[130px]",
      },
    ];
  }, [isVisitLogMode, isMonthlyView, showChart]); // Added showChart to dependencies
  // Calculate dynamic height for chart to match desired UI
  // 40px per bar item + 30px buffer. Minimum height 350px.
  if (loading)
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-sahi-primary-color mr-3" />
        <span className="text-text-color">Loading interaction...</span>
      </div>
    );
  if (error)
    return (
      <div className="p-6">
        <Card className="border-sahi-red bg-red-50">
          <CardContent className="p-4">
            <p className="text-sahi-red font-medium">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  return (
    <TooltipProvider>
      <div className="space-y-3 lg:space-y-6 ">
        {/* Desktop Header - Aligned with Expenses Style */}
        <div className="hidden lg:block">
          <div className="flex justify-between items-center gap-3">
            <div className="flex items-center flex-grow">
              <h1 className="text-3xl font-bold text-text-color mr-4 web-header-name">
                {pageTitle}
              </h1>
              <div className="flex items-center justify-end flex-grow">
                {/* Search Bar (Show if > 10 records or searching) */}
                {(filteredRecords.length > 10 || searchTerm) && (
                  <div className="relative w-120 sahi-radius filter-border">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-dropdown-white" />
                    <Input
                      placeholder="Search"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 h-10 border-none focus:outline-none focus:ring-0 shadow-none"
                    />
                  </div>
                )}
              </div>
            </div>
            {/* <div className="flex items-center gap-3">
               <Button onClick={() => setRecEntry(true)} className="sahi-primary-button">
        <Plus className="h-4 w-4 mr-1" />
        Add Record
      </Button> 
            </div> */}
            <div className="flex items-center gap-3 h-full">

              <Button
                className={cn(
                  "button-config sahi-primary-button flex items-center justify-center group px-2.5 py-[5px] ",
                  isFilterSelected && "filter-active"
                )}
                onClick={() => setIsFilterSelected(prev => !prev)}
                aria-label="Toggle filter"
              >
                <div className="h-6 w-6 flex items-center justify-center transition-all">
                  <Filter
                    className={cn(
                      "h-4 w-4",
                      isFilterSelected
                        ? "text-sahi-primary-color"
                        : "text-white group-hover:text-sahi-primary-color"
                    )}
                  />
                </div>
              </Button>

            </div>
          </div>
        </div>
        {/* Mobile Header */}
        <ConditionalDiv className="flex flex-col lg:hidden gap-3 ">
          {/* Search Bar (Show if > 10 records or searching) */}
          {(filteredRecords.length > 10 || searchTerm) && (
            <div className="w-full flex items-center gap-2">
              <div className="relative flex-grow">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-dropdown-white" />
                <Input
                  value={searchTerm}
                  placeholder="Search"
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 h-10 w-full"
                />
              </div>
              {/* <FilterButton /> */}
            </div>
          )}
        </ConditionalDiv>
        {/* Filters Bar - Aligned with Expenses Layout */}
        {recordsData && isFilterSelected && (
          <ConditionalDiv
            className={cn(
              "flex flex-col-reverse mt-5",
              recordsData.length === 0 && "flex-wrap"
            )}
          >
            {/* Filters Bar */}
            {isFilterSelected && !isMobile && (
              <div className="flex flex-wrap gap-4">
                {/* Period Filter Component */}
                <PeriodFilter
                  value={selectedFilters}
                  onChange={(newFilters) => {
                    setSelectedFilters(newFilters);
                    setTempFilters(newFilters);
                  }}
                  oldestDate={oldestSinceDT}
                  isRecordsMode={true}
                  periodOptions={["Today", "Yesterday", "Other Date", "This Week", "Last Week", "This Month", "Other Month"]}
                  showMonthFilter={true}
                  showQuarterFilter={false}
                  showYearFilter={false}
                  showDateFilter={true}
                />


                {/* Other Filters (Executive, Contact, Type) */}
                {filterOptions.map((item) => {
                  if (item.mobileOnly) return null;
                  if (["dateRange", "otherMonth", "otherQuarter", "otherYear", "otherDate"].includes(item.key)) return null;
                  if (isVisitLogMode && item.key === "recordType") return null;

                  return (
                    <CommonFilter
                      key={item.key}
                      label={item.label}
                      options={item.options}
                      value={selectedFilters[item.key]}
                      onChange={(value) => {
                        setSelectedFilters((prev) => ({
                          ...prev,
                          [item.key]: value,
                        }));
                        setTempFilters((prev) => ({
                          ...prev,
                          [item.key]: value,
                        }));
                      }}
                      displayValue={item.displayValue}
                      disabled={item.disabled}
                      searchEnabled={item.searchEnabled}
                      showClearButton={true}
                      mobileLabel={item.mobileLabel}
                    />
                  );
                })}
              </div>
            )}
          </ConditionalDiv>
        )}
        {/* Mobile Filter Drawer */}
        {isFilterSelected && isMobile && (
          <Drawer
            open={isDrawerOpen}
            onOpenChange={(open) => {
              setIsDrawerOpen(open);
              if (!open) {
                setIsFilterSelected(false);
              }
            }}
          >
            <DrawerContent className="p-0 [&>div:first-child]:hidden">
              <div className="flex flex-col h-[100dvh]">
                <div className="flex plain items-center justify-between p-4 border-b-2 border-gray-200 text-xl !rounded-t-[10px]">
                  <p className="text-lg font-semibold text-sahi-primary-color">
                    Record Filters
                  </p>
                  <button
                    onClick={handleResetFilters}
                    className="group flex items-center gap-1 text-sm font-light hover:underline text-sahi-primary-color"
                  >
                    <RiResetLeftFill className="group-hover:rotate-[-20deg] transition-transform duration-200" />
                    <span>Reset</span>
                  </button>
                </div>
                <div className="flex flex-1 overflow-y-auto">
                  <div className="flex-1 p-4 overflow-y-auto filter-scroll-container">
                    {filterOptions.map((item) => {
                      // Hide Type Filter if in Visit Log Mode
                      if (isVisitLogMode && item.key === "recordType")
                        return null;
                      return (
                        <div key={item.key} id={item.key} className="mb-4">
                          <DrawerFilter
                            id={item.key}
                            label={item.mobileLabel || item.label}
                            options={item.options}
                            value={tempFilters[item.key]}
                            displayValue={item.displayValue}
                            onChange={(value) => {
                              setTempFilters((prev) => {
                                const newState = { ...prev, [item.key]: value };

                                if (item.key === "dateRange" && value === "Other Month") {
                                  if (prev.otherMonth === "all" || !prev.otherMonth) {
                                    newState.otherMonth = getCurrentMonthYear();
                                  }
                                }

                                if (item.key === "dateRange" && value === "Other Date") {
                                  // Only set default if there's no valid date
                                  if (!prev.otherDate || prev.otherDate === "all") {
                                    newState.otherDate = getTodayDateStr();
                                  }
                                }

                                // If we're selecting a date directly, don't reset it
                                if (item.key === "otherDate" && value !== "all") {
                                  newState.otherDate = value;
                                }

                                return newState;
                              });
                            }}
                            sortConfig={tempSortConfig}
                            setTempsortConfig={setTempSortConfig}
                            tempsortConfig={tempSortConfig}
                            sortOptionMap={mobileSortMap}
                            type={item.type}
                            disabled={item.mobiledisabled}
                            searchEnabled={item.searchEnabled}
                            mobileDropdownText={item.mobileDropdownText}
                            mobileLabel={item.mobileLabel}
                          />

                        </div>
                      );
                    })}
                  </div>
                </div>
                <DrawerFooter className="sticky bottom-0 border-t-2 p-3 bg-white">
                  <DrawerClose>
                    <div className="flex justify-start gap-2">
                      <Button
                        className="sahi-primary-button"
                        onClick={() => {
                          setSelectedFilters(tempFilters);

                          sortData(tempSortConfig.key, tempSortConfig.direction);
                          setIsDrawerOpen(false);
                          setIsFilterSelected(false);

                          const currentParams = new URLSearchParams();
                          const urlMap = {
                            connID: "cid",
                            executive: "eid",
                            recordType: "iid",
                            dateRange: "p",
                            otherMonth: "om",
                            otherQuarter: "oq",
                            otherYear: "oy",
                          };
                          Object.entries(urlMap).forEach(([stateKey, urlKey]) => {
                            const val = tempFilters[stateKey];
                            if (val && val !== "all") currentParams.set(urlKey, val);
                          });
                          navigate(`${location.pathname}?${currentParams.toString()}`, { replace: true });
                        }}
                      >
                        Apply
                      </Button>
                      <Button
                        variant="outline"
                        className="sahi-secondary-button"
                        onClick={handleCancel}
                      >
                        Cancel
                      </Button>
                    </div>
                  </DrawerClose>
                </DrawerFooter>
              </div>
            </DrawerContent>
          </Drawer>
        )}
        {/* --- MAIN SPLIT VIEW: Table & Chart --- */}
        <div
          className={cn(
            "grid grid-cols-1 gap-2 w-full items-start",
            showChart ? "lg:grid-cols-[1fr_700px]" : "lg:grid-cols-1"
          )}
        >
          {/* RIGHT COLUMN (Chart) */}
          {showChart && (
            <div className="w-full order-1 lg:order-2 mb-4 lg:mb-0">
              <Card className="bg-sahi-white shadow-sm border border-gray-200 sticky top-4 sahi-radius overflow-hidden !p-0 -mb-3">
                <CardHeader className="h-11 py-0 px-4 flex flex-row items-center justify-center border-b border-gray-100 bg-white">
                  <CardTitle className="text-sm font-semibold">
                    {isVisitLogMode ? "Executive vs Visit Logs" : "Executive Efforts"}
                  </CardTitle>
                </CardHeader>

                <CardContent className="p-0">
                  {/* Chart Filters / Legend */}
                  {selectedFilters.recordType === "all" && (
                    <div
                      className="flex flex-wrap gap-2 pt-3" // Changed mb-6 to pt-3 to add tight padding from header
                      style={{ marginLeft: "15px", paddingBottom: "10px" }} // Matches YAxis width exactly
                    >
                      {dynamicActivityTypes.map((type) => {
                        const isActive = activeChartFilters.includes(type);
                        const color = dynamicColorMap[type];
                        return (
                          <Badge
                            key={type}
                            onClick={() => toggleChartFilter(type)}
                            className={cn(
                              "cursor-pointer px-3 py-1 text-[11px] font-semibold transition-all select-none border-2 rounded-full shadow-sm",
                              isActive
                                ? "text-white border-transparent ring-2 ring-offset-1"
                                : "bg-gray-100 text-gray-600 border-gray-100 hover:border-sahi-primary-color/30 hover:bg-gray-50"
                            )}
                            style={{
                              backgroundColor: isActive ? color : undefined,
                              borderColor: isActive ? color : undefined,
                              boxShadow: isActive ? `0 4px 6px ${color}40` : "none",
                            }}
                          >
                            <span className="flex items-center gap-1.5">
                              {!isActive && (
                                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
                              )}
                              {type}
                            </span>
                          </Badge>
                        );
                      })}
                      {activeChartFilters.length > 0 && (
                        <Badge
                          onClick={() => setActiveChartFilters([])}
                          variant="outline"
                          className="cursor-pointer px-3 py-1 text-[11px] text-gray-400 hover:text-red-500 border-dashed rounded-full"
                        >
                          Clear
                        </Badge>
                      )}
                    </div>
                  )}
                </CardContent>

                <CardContent className="p-0">
                  <div className="w-full p-2 text-xs">
                    <div
                      style={{
                        height: `${(processedChartData.length * 45) + 30}px`, // Reduced buffer from 60 to 30
                        minHeight: processedChartData.length > 0 ? "80px" : "0px",
                        position: "relative",
                      }}
                    >
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          layout="vertical"
                          data={processedChartData}
                          margin={{ top: 0, right: 45, left: 0, bottom: 5 }}
                          accessibilityLayer={false}
                          style={{ outline: "none" }}
                        >
                          <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f0f0f0" />
                          <XAxis type="number" hide />
                          <YAxis
                            dataKey="name"
                            type="category"
                            width={60} // This matches the filter's marginLeft
                            tickLine={false}
                            axisLine={false}
                            tick={(props) => {
                              const { x, y, payload } = props;
                              return (
                                <g transform={`translate(${x},${y})`}>
                                  <text
                                    x={-8}
                                    y={0}
                                    dy={4}
                                    textAnchor="end"
                                    fill="#6b7280"
                                    style={{ fontSize: 11, outline: 'none', userSelect: 'none' }}
                                  >
                                    {payload.value}
                                  </text>
                                </g>
                              );
                            }}
                          />
                          <RechartsTooltip
                            content={<CustomTooltip />}
                            cursor={{ fill: 'transparent' }}
                            allowEscapeViewBox={{ x: false, y: false }} // Prevent tooltip from going outside chart area
                            useTranslate3d={true} // Smoother postioning
                            coordinate={{ x: 0, y: 0 }} // Initial coordinate help
                          />
                          {[...Array(maxSegments)].map((_, i) => (
                            <Bar
                              key={`stack${i}`}
                              dataKey={`stack${i}`}
                              stackId="a"
                              barSize={24}
                              animationDuration={500}
                              style={{ outline: "none" }}
                            >
                              {processedChartData.map((entry, index) => (
                                <Cell
                                  key={`cell-${index}`}
                                  fill={entry[`stack${i}Color`] || "transparent"}
                                />
                              ))}
                              <LabelList
                                dataKey={`stack${i}`}
                                content={(props) => {
                                  const { x, y, width, height, index } = props;
                                  const row = processedChartData[index];
                                  if (!row) return null;
                                  const isLastVisibleSegment = row[`stack${i + 1}`] === undefined || row[`stack${i + 1}`] === 0;
                                  if (!isLastVisibleSegment || row.total === 0) return null;
                                  return (
                                    <text x={x + width + 8} y={y + height / 2} fill="#374151" fontSize="10" fontWeight="bold" dominantBaseline="middle">
                                      {row.total}
                                    </text>
                                  );
                                }}
                              />
                            </Bar>
                          ))}
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
          {/* LEFT COLUMN (Table) */}
          <div className="w-full order-2 lg:order-1 min-w-0">
            {filteredRecords.length > 0 ? (
              <>
                <div className="hidden md:block">
                  {/* Changed from lg:block to w-full */}
                  <Card className="bg-sahi-white w-full !pt-0 border border-gray-200 shadow-sm sahi-radius overflow-hidden">
                    <CardContent className="px-0 !sahi-radius">
                      <div className="overflow-x-auto">
                        <Table className="w-full table-fixed"> {/* Changed to table-fixed or leave as w-full */}
                          <TableHeader>
                            <TableRow className="bg-gray-50/50 py-1.5 h-11 border-b">
                              {tableHeaderCols.map(
                                ({ key, label, className }) => (
                                  <TableHead
                                    key={key}
                                    className={cn(
                                      "h-11 px-4 text-sm font-semibold text-black bg-white",
                                      className
                                    )}
                                    onClick={() => sortData(key)}
                                  >
                                    <div className="flex items-center cursor-pointer gap-1">
                                      {label}
                                      {!["threadIDs"].includes(key) &&
                                        sortConfig.key === key &&
                                        sortConfig.direction &&
                                        (sortConfig.direction === "asc" ? (
                                          <ArrowUp className="ml-1 h-4 w-4 text-black" />
                                        ) : (
                                          <ArrowDown className="ml-1 h-4 w-4 text-black" />
                                        ))}
                                    </div>
                                  </TableHead>
                                )
                              )}
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {currentRecords.map((record, index) => (
                              <TableRow
                                key={record.recordID || index}
                                className="hover:bg-gray-50 cursor-pointer h-10 align-middle "                              >
                                {tableHeaderCols.map((col) => {
                                  // Shared class for strict alignment and no wrapping
                                  const cellClass = cn(
                                    "h-11 px-4 text-sm text-text-color text-left align-middle whitespace-nowrap overflow-hidden",
                                    col.className
                                  );
                                  if (col.key === "bizName") {
                                    // Use BizName if available, otherwise fall back to ContactName, otherwise show "-"
                                    const displayName = record.bizName?.trim() || record.contactName?.trim() || "-";

                                    return (
                                      <TableCell
                                        key="biz"
                                        className={cellClass}
                                      >
                                        <div
                                          className={cn(
                                            "break-words",
                                            isMonthlyView ? "whitespace-normal line-clamp-1 w-full" : "max-w-[300px] truncate"
                                          )}
                                          title={displayName}
                                        >
                                          {displayName}
                                        </div>
                                      </TableCell>
                                    );
                                  }
                                  if (col.key === "recType") {
                                    const hasLoc = isValidCoordinate(
                                      record.loc
                                    );
                                    return (
                                      <TableCell
                                        key="loc"
                                        className={cellClass}
                                      >
                                        <div className="flex items-center gap-2">
                                          {/* Logic:
                  1. If Visit Log Mode: Don't show text, only show MapPin if available.
                  2. If Effort Mode: Show Type text + MapPin if available.
                */}
                                          {!isVisitLogMode && (
                                            <span className="truncate">
                                              {record.recType || "-"}
                                            </span>
                                          )}
                                          {hasLoc ? (
                                            <MapPin
                                              className="h-3.5 w-3.5 text-sahi-blue cursor-pointer flex-shrink-0"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                                                  record.loc
                                                )}`;
                                                window.open(mapsUrl, "_blank");
                                              }}
                                            />
                                          ) : (
                                            // Show dash if in visit mode and no location is present
                                            isVisitLogMode && (
                                              <span className="text-gray-400">
                                                -
                                              </span>
                                            )
                                          )}
                                        </div>
                                      </TableCell>
                                    );
                                  }
                                  if (col.key === "record") {
                                    return (
                                      <TableCell
                                        key="notes"
                                        className={cellClass}
                                      >
                                        <div
                                          className={cn(
                                            "break-words",
                                            // When chart is hidden, allow notes to be much wider
                                            !showChart ? "whitespace-normal min-w-[300px]" : "max-w-[250px] truncate"
                                          )}
                                          title={record.record}
                                        >
                                          {record.record || "-"}
                                        </div>
                                      </TableCell>
                                    );
                                  }
                                  if (col.key === "recDT") {
                                    return (
                                      <TableCell
                                        key="date"
                                        className={cellClass}
                                      >
                                        {getRelativeDateText(record.recDT)}
                                      </TableCell>
                                    );
                                  }
                                  if (col.key === "recUserID") {
                                    return (
                                      <TableCell
                                        key="exec"
                                        className={cellClass}
                                      >
                                        <div className="truncate">
                                          {getExecutiveName(record.recUserID)}
                                        </div>
                                      </TableCell>
                                    );
                                  }
                                  return null;
                                })}
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </CardContent>
                  </Card>
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(size) => setRowsPerPage(size)}
                    onPageChange={(page) => setCurrentPage(page)}
                  />
                </div>
                <div className="md:hidden">
                  {currentRecords.map((record, index) => (
                    <MobileRecordCard key={record.recordID || index} record={record} index={index} />
                  ))}
                </div>
              </>
            ) : (
              <div className={cn("flex flex-col items-start", isMobile ? "" : "px-0 lg:px-0")}>
                {/* SearchTerm OR any active filter (like Yesterday) will show this box */}
                {(searchTerm.trim() !== "" || hasActiveFilters) ? (
                  <div className="f-col items-start gap-5 ">
                    <p className="no-records border border-gray-200 shadow-sm sahi-radius bg-white p-4">
                      <span>No data found for selected filters.</span>
                      <button
                        onClick={handleResetFilters}
                        className="text-sahi-primary-color underline hover:text-sahi-primary-color/80 cursor-pointer ml-2"
                      >
                        Clear filter.
                      </button>
                    </p>
                  </div>

                ) : (
                  // Only shows the clean description icon/text if absolutely no filters are applied
                  renderEmptyState()
                )}
              </div>
            )}
          </div>
        </div>

        <ConnectionEntryDialog
          open={isConnectionDialogOpen}
          onOpenChange={setIsConnectionDialogOpen}
          triggerButton={false}
        />
        <RecordEntry
          triggerButton={false}
          open={recEntry}
          onOpenChange={setRecEntry}
        />
      </div>
    </TooltipProvider>
  );
};
export default Records;

